import { BASE_CSS } from './TradeStyles';

/**
 * The home page's stylesheet: the section rhythm, the category tiles, the product rail, the
 * trade band, the numbered assurance register, plus the offer slider and kit cards that other
 * blocks still draw — restyled to "Horizon" as approved in
 * `solar/docs/design/redesign-2026-10-01/` (`css/tokens.css`, `css/shared.css`, `index.html`).
 *
 * <p>Every colour is a theme token (`--color-*` through the `--ae-*` scope), and nothing here
 * chooses a colour of its own: the only values beside `var()` lookups are `color-mix()` blends
 * of those same variables. `build-palette.py` owns the values; the hex fallbacks live in the
 * base layer (`TradeStyles.BASE_CSS`), never here. Every rule is scoped under `.ae-`, so
 * nothing reaches the platform's own blocks.
 *
 * <p><b>The on-dark bands.</b> Horizon paints the trade band and the assurance register as
 * dark ink bands while the selling surfaces stay light ("dark ink is the brand voice, light
 * paper is the selling surface"). Their colours resolve through the `--ae-band-*` scope
 * variables below, which read the palette's per-mood leaves first, under the names
 * `build-palette.py` actually emits (`themeToCss` names a leaf `--` + its tree path, so
 * `color.band.mid` is `--color-band-mid`): the band surfaces by ROLE
 * (`--color-band-mid` for the trade band, `--color-band-register`, `--color-band-raised`
 * for fields and dark inputs), the on-band text and hairlines (`--color-text-inverse`,
 * `--color-text-inverse-muted`, `--color-border-inverse`, `--color-border-inverse-strong`,
 * as the hero scope in `shopIntroCss.ts` reads them), `--color-accent-on-band` for amber
 * type on a band (THE mood flip for amber), and `--color-text-on-accent` /
 * `--color-brand-700` for the navy action buttons — and fall back to derivations of the
 * base `--ae-*` scope so a shop whose palette has not authored them yet still renders the
 * dark mood correctly. Both palette patches author every one of these leaves in both moods
 * (Horizon dark: the ink ramp #0A1424/#11203A with #E8EEF7/#9FB0C8 text and sun-500
 * accents; Horizon Day: the mist ladder #E9EFF6/#DCE5F0, white register, dark text,
 * sun-700 accents — `css/skin-light.css` is the contract), which is what lets the Day
 * merge flip these bands. `test/tokens.test.ts` pins every name read here to a leaf both
 * palettes emit.
 */
export const HOME_CSS = `
${BASE_CSS}

/* ── Horizon scope extension: on-dark band + button-ink values ───────────────
 * Token first, then a derivation of the base scope — never a literal colour. */
.ae{
  --ae-btn-ink:var(--color-text-on-accent,var(--ae-ink));
  --ae-btn-ink-hover:var(--color-brand-700,var(--ae-link-hover));
  --ae-band:var(--color-band-mid,var(--ae-ink));
  --ae-register:var(--color-band-register,var(--ae-band));
  --ae-band-ink:var(--color-text-inverse,var(--ae-surface));
  --ae-band-muted:var(--color-text-inverse-muted,color-mix(in srgb,var(--ae-band-ink) 64%,var(--ae-band)));
  --ae-band-raised:var(--color-band-raised,color-mix(in srgb,var(--ae-band-ink) 7%,var(--ae-band)));
  --ae-band-field:var(--color-band-raised,var(--ae-band));
  --ae-band-line:var(--color-border-inverse,color-mix(in srgb,var(--ae-band-ink) 12%,transparent));
  --ae-band-line-strong:var(--color-border-inverse-strong,color-mix(in srgb,var(--ae-band-ink) 28%,transparent));
  --ae-band-accent:var(--color-accent-on-band,var(--ae-accent));
  --ae-band-focus:var(--ae-band-accent);
  --ae-radius-xl:var(--radius-2xl,16px);
}

.ae-wrap{max-width:var(--ae-max);margin-inline:auto;padding-inline:var(--ae-gutter)}
.ae-sec{padding-block:clamp(36px,5vw,64px) clamp(28px,4vw,48px)}
.ae-sec__head{display:flex;flex-wrap:wrap;align-items:flex-end;justify-content:space-between;gap:16px 24px;margin-bottom:24px}
.ae-sec__title{margin:0;font-size:clamp(1.625rem,1.1rem + 1.8vw,2.375rem);line-height:1.12;font-weight:680;letter-spacing:-.02em;color:var(--ae-ink)}
.ae-sec__more{font-size:.9375rem;font-weight:600;color:var(--ae-link);text-decoration:none;white-space:nowrap}
.ae-sec__more:hover{color:var(--ae-link-hover);text-decoration:underline}

/* The Horizon eyebrow: plain uppercase type in the accent's TEXT form — sun-700 on light
   surfaces, never sun-500 (2.0:1 as text). On the dark bands it re-colours to the band
   accent, where the luminous form is legal. */
.ae-eyebrow{display:block;align-self:flex-start;margin:0 0 8px;padding:0;border:0;background:none;
  font-size:.75rem;font-weight:650;letter-spacing:.08em;text-transform:uppercase;line-height:1.3;
  color:var(--ae-accent-ink)}

/* ── Buttons ─────────────────────────────────────────────────────────────── */
.ae-btn{display:inline-flex;align-items:center;justify-content:center;gap:10px;min-height:44px;padding:0 24px;
  border-radius:var(--radius-md,10px);border:1px solid transparent;font:inherit;font-size:1rem;font-weight:650;
  text-decoration:none;cursor:pointer;transition:background .15s ease,border-color .15s ease,color .15s ease,transform .15s ease}
.ae-btn:disabled{opacity:.55;cursor:not-allowed}
.ae-btn--accent{background:var(--ae-accent);color:var(--ae-on-accent)}
.ae-btn--accent:hover:not(:disabled){background:var(--ae-accent-deep);transform:translateY(-1px)}
.ae-btn--brand{background:var(--ae-btn-ink);color:var(--ae-on-brand)}
.ae-btn--brand:hover:not(:disabled){background:var(--ae-btn-ink-hover);color:var(--ae-on-brand)}
.ae-btn--ghost{background:transparent;border-color:var(--ae-border-strong);color:var(--ae-ink)}
.ae-btn--ghost:hover{border-color:var(--ae-muted);color:var(--ae-ink)}
.ae-add-status{font-size:.8125rem;color:var(--ae-muted);min-height:1em}
.ae-add-status a{color:var(--ae-link);font-weight:600}
@media(prefers-reduced-motion:reduce){.ae-btn,.ae-btn--accent:hover:not(:disabled){transition:none;transform:none}}

/* ── Live price — tabular figures, the published basis large ─────────────── */
.ae-price{display:flex;flex-wrap:wrap;align-items:baseline;column-gap:6px;row-gap:2px}
.ae-price__now{font-size:1.375rem;line-height:1.2;font-weight:700;letter-spacing:-.01em;
  font-variant-numeric:tabular-nums;color:var(--ae-ink)}
.ae-price--lg .ae-price__now{font-size:clamp(1.6rem,1.2rem + 1.2vw,2.125rem)}
.ae-price__basis{font-size:.8125rem;font-weight:500;color:var(--ae-muted)}
.ae-price__alt{flex-basis:100%;font-size:.8125rem;color:var(--ae-muted);font-variant-numeric:tabular-nums}
.ae-price--loading{height:2.75rem;width:9rem;border-radius:var(--ae-radius);background:var(--ae-subtle)}

/* ── Offer slider (the SolarOfferSlider block; not recomposed by Horizon) ── */
.ae-slider{position:relative;margin-top:clamp(12px,2vw,20px);border:1px solid var(--ae-border);
  border-radius:var(--ae-radius-xl);overflow:hidden;background:var(--ae-surface);
  display:grid}
.ae-slide{grid-area:1/1;position:relative;display:flex;align-items:center;min-height:clamp(360px,34vw,460px);
  padding:clamp(28px,4vw,56px);padding-bottom:clamp(64px,5vw,72px);
  opacity:0;visibility:hidden;transition:opacity .6s ease,visibility 0s linear .6s}
.ae-slide.is-on{opacity:1;visibility:visible;transition:opacity .6s ease}
.ae-slide__media{position:absolute;inset:0}
.ae-slide__media img,.ae-slide__media .ae-scene{width:100%;height:100%;display:block;object-fit:cover}
.ae-slide__media::after{content:"";position:absolute;inset:0;
  background:linear-gradient(90deg,var(--ae-surface) 0%,color-mix(in srgb,var(--ae-surface) 90%,transparent) 36%,transparent 64%)}
.ae-slide__body{position:relative;z-index:1;max-width:560px;display:flex;flex-direction:column;gap:12px}
.ae-slide__title{margin:0;font-size:clamp(1.75rem,1.2rem + 1.9vw,2.5rem);line-height:1.08;letter-spacing:-.025em;color:var(--ae-ink)}
.ae-slide__text{margin:0;font-size:clamp(1rem,.95rem + .2vw,1.0625rem);color:var(--ae-muted)}
.ae-facts{display:flex;flex-wrap:wrap;gap:6px 18px;margin:0;padding:0;list-style:none;font-size:.875rem;color:var(--ae-muted)}
.ae-facts b{color:var(--ae-ink)}
.ae-actions{display:flex;flex-wrap:wrap;align-items:center;gap:10px;margin-top:6px}
.ae-slider__dots{position:absolute;left:clamp(28px,4vw,56px);bottom:22px;z-index:2;display:flex;gap:8px}
.ae-slider__dot{width:28px;height:4px;padding:0;border:0;border-radius:2px;background:var(--ae-border-strong);cursor:pointer}
.ae-slider__dot[aria-current="true"]{background:var(--ae-accent)}
.ae-slider__arrows{position:absolute;right:18px;bottom:14px;z-index:2;display:flex;gap:6px}
.ae-slider__arrow{width:38px;height:38px;border-radius:50%;border:1px solid var(--ae-border);background:var(--ae-surface);
  color:var(--ae-muted);font-size:1.25rem;line-height:1;cursor:pointer}
.ae-slider__arrow:hover{color:var(--ae-link);border-color:var(--ae-border-strong)}
@media (max-width:760px){
  .ae-slide{align-items:flex-end;min-height:520px;padding:20px 20px 64px}
  .ae-slide__media::after{background:linear-gradient(0deg,var(--ae-surface) 0%,color-mix(in srgb,var(--ae-surface) 92%,transparent) 55%,transparent 85%)}
  .ae-slider__dots{left:20px}
}
@media (prefers-reduced-motion:reduce){.ae-slide,.ae-slide.is-on{transition:none}}

/* The drawn scenes, coloured by the tokens so a recoloured shop recolours them too. */
.ae-scene__sky{fill:var(--ae-subtle)}
.ae-scene__sun{fill:var(--ae-accent)}
.ae-scene__sun-glow{fill:var(--ae-accent);opacity:.18}
.ae-scene__ground{fill:var(--ae-border)}
.ae-scene__wall,.ae-scene__unit{fill:var(--ae-surface);stroke:var(--ae-border-strong);stroke-width:2}
.ae-scene__window,.ae-scene__screen{fill:var(--ae-tint);stroke:var(--ae-border-strong);stroke-width:2}
.ae-scene__door{fill:var(--ae-border);stroke:var(--ae-border-strong)}
.ae-scene__roof{fill:var(--ae-muted)}
.ae-scene__panel,.ae-scene__stack{fill:var(--ae-brand);stroke:var(--ae-tint);stroke-width:1.5}
.ae-scene__line{stroke:var(--ae-border-strong);stroke-width:2;fill:none}
.ae-scene__stack + .ae-scene__line{stroke:var(--ae-tint)}
.ae-scene__pallet{fill:var(--ae-accent-deep)}
.ae-scene__led{fill:var(--ae-success)}
.ae-scene__bolt{stroke:var(--ae-accent);stroke-width:4;fill:none}
.ae-scene__cable{stroke:var(--ae-muted);stroke-width:3;fill:none}
.ae-scene__wheel{fill:var(--ae-muted)}

/* ── Category tiles — Horizon's department tiles ─────────────────────────────
 * Compact horizontal rows on phones; from 768px, three-up cards with the line
 * drawing above the name and the arrow pinned top-right. Hover lifts the card. */
.ae-tiles{display:grid;grid-template-columns:1fr;gap:12px;margin:0;padding:0;list-style:none}
.ae-tile{display:flex;align-items:center;gap:16px;height:100%;padding:16px;background:var(--ae-surface);
  border:1px solid var(--ae-border);border-radius:var(--ae-radius-xl);box-shadow:var(--ae-shadow-sm);
  color:var(--ae-ink);text-decoration:none;
  transition:transform .18s ease,box-shadow .18s ease,border-color .18s ease}
.ae-tile:hover{transform:translateY(-3px);box-shadow:var(--ae-shadow);border-color:var(--ae-border-strong);color:var(--ae-ink)}
.ae-tile__icon{flex:none;display:grid;place-items:center;width:48px;height:48px}
.ae-tile__body{min-width:0;display:grid;gap:3px}
.ae-tile__name{display:block;font-size:1.0625rem;line-height:1.35;font-weight:650;letter-spacing:-.01em}
.ae-tile__note{display:block;font-size:.8125rem;line-height:1.5;color:var(--ae-muted)}
.ae-tile__arrow{margin-left:auto;color:var(--ae-link);font-weight:650;transition:transform .15s ease}
.ae-tile:hover .ae-tile__arrow{transform:translateX(3px)}
.ae-icon{width:100%;height:100%;fill:none;stroke:var(--ae-btn-ink);stroke-width:1.8;stroke-linecap:round;stroke-linejoin:round}
.ae-icon .ae-icon__fill{fill:var(--ae-surface)}
@media(min-width:768px){
  .ae-tiles{grid-template-columns:repeat(3,1fr);gap:16px}
  .ae-tile{position:relative;flex-direction:column;align-items:flex-start;padding:24px;gap:16px}
  .ae-tile__arrow{position:absolute;top:24px;right:24px;margin-left:0}
}
@media(min-width:1024px){.ae-tiles{gap:24px}}
@media(prefers-reduced-motion:reduce){.ae-tile,.ae-tile__arrow{transition:none}.ae-tile:hover{transform:none}}

/* ── Product rail — Horizon cards: media stage, hairline-ruled price foot,
 *    the variant-C navy Add (owner-chosen 2026-10-01). Price-per-watt and datasheet
 *    links stay off these cards until the listing API returns attributes and
 *    document links (follow-up F4 in the go-live plan). ──────────────────── */
.ae-rail{display:grid;grid-template-columns:1fr;gap:16px;margin:0;padding:0;list-style:none}
@media(min-width:768px){.ae-rail{grid-template-columns:repeat(2,1fr);gap:24px}}
@media(min-width:1024px){.ae-rail{grid-template-columns:repeat(4,1fr)}}
.ae-rail > li{display:flex;min-width:0}
.ae-card{flex:1;display:flex;flex-direction:column;background:var(--ae-surface);border:1px solid var(--ae-border);
  border-radius:var(--ae-radius-lg);box-shadow:var(--ae-shadow-sm);overflow:hidden;
  transition:transform .18s ease,box-shadow .18s ease,border-color .18s ease}
.ae-card:hover{transform:translateY(-3px);box-shadow:var(--ae-shadow);border-color:var(--ae-border-strong)}
.ae-card--loading{min-height:320px;background:var(--ae-subtle);border-color:transparent;box-shadow:none}
.ae-card__link{display:flex;flex-direction:column;color:var(--ae-ink);text-decoration:none}
.ae-card__media{display:grid;place-items:center;height:200px;padding:24px;border-bottom:1px solid var(--ae-border);
  background:var(--ae-surface);overflow:hidden}
.ae-card__media img{max-width:100%;max-height:160px;width:auto;object-fit:contain}
.ae-card__media .ae-icon{width:56%;height:56%;stroke-width:1.2}
.ae-card__text{display:flex;flex-direction:column;gap:6px;padding:16px 16px 12px}
.ae-card__brand{font-size:.75rem;font-weight:650;text-transform:uppercase;letter-spacing:.08em;color:var(--ae-muted)}
.ae-card__title{font-size:1.0625rem;font-weight:640;line-height:1.35;letter-spacing:-.01em;min-height:2.7em;
  display:-webkit-box;-webkit-line-clamp:2;-webkit-box-orient:vertical;overflow:hidden}
.ae-card__link:hover .ae-card__title{color:var(--ae-link)}
.ae-card__foot{margin-top:auto;display:flex;flex-direction:column;gap:10px;padding:12px 16px 16px;border-top:1px solid var(--ae-border)}
.ae-card__stock{display:flex;align-items:center;gap:5px;font-size:.8125rem;font-weight:500;color:var(--ae-muted)}
.ae-card__stock[data-availability="IN_STOCK"]{color:var(--ae-success)}
.ae-card__stock::before{content:"";width:7px;height:7px;border-radius:50%;background:currentColor}
.ae-card__stock--out{color:var(--ae-muted)}
.ae-card__add{display:inline-flex;align-items:center;justify-content:center;gap:8px;width:100%;min-height:44px;
  border:0;border-radius:var(--ae-radius);background:var(--ae-btn-ink);color:var(--ae-on-brand);
  font:inherit;font-size:.9375rem;font-weight:600;white-space:nowrap;cursor:pointer;transition:background .15s ease}
.ae-card__add:hover:not(:disabled){background:var(--ae-btn-ink-hover)}
.ae-card__add:disabled{opacity:.55;cursor:not-allowed}
.ae-card__add svg{flex:none}
@media(prefers-reduced-motion:reduce){.ae-card{transition:none}.ae-card:hover{transform:none}}

/* ── Kit cards (the SolarKitCards block; off the home page until kit pricing
 *    is decided — owner decision #1) ─────────────────────────────────────── */
.ae-kits{display:grid;grid-template-columns:repeat(auto-fill,minmax(min(300px,100%),1fr));gap:12px}
.ae-kit{display:flex;flex-direction:column;gap:8px;padding:18px;border:1px solid var(--ae-border);
  border-radius:var(--ae-radius-lg);background:var(--ae-surface);box-shadow:var(--ae-shadow-sm)}
.ae-kit__name{font-size:.75rem;font-weight:650;color:var(--ae-muted);text-transform:uppercase;letter-spacing:.08em}
.ae-kit__size{font-size:1.5rem;font-weight:700;letter-spacing:-.02em;color:var(--ae-ink);font-variant-numeric:tabular-nums}
.ae-kit__lines{margin:0;padding-left:18px;font-size:.875rem;color:var(--ae-muted)}
.ae-kit__foot{margin-top:auto;display:flex;flex-direction:column;gap:10px;padding-top:6px}

/* ── Trade band — Horizon's full-bleed ink band ──────────────────────────────
 * The band paints the block root edge to edge; the grid inside keeps the page
 * column. In Horizon Day the same rules resolve to the mist ladder through the
 * band tokens. */
.ae-trade{background:var(--ae-band);color:var(--ae-band-ink);padding-block:clamp(48px,6vw,64px)}
.ae-trade__grid{display:grid;grid-template-columns:1fr;gap:32px;align-items:center}
@media(min-width:1024px){.ae-trade__grid{grid-template-columns:minmax(0,1.1fr) minmax(0,.9fr);gap:64px}}
.ae-trade .ae-eyebrow{color:var(--ae-band-accent);margin-bottom:12px}
.ae-trade__title{margin:0;font-size:clamp(1.625rem,1.1rem + 1.8vw,2.375rem);line-height:1.12;font-weight:680;
  letter-spacing:-.02em;color:var(--ae-band-ink)}
.ae-trade__text{margin:12px 0 0;font-size:1rem;line-height:1.6;color:var(--ae-band-muted);max-width:44em}
.ae-trade__facts{list-style:none;display:grid;gap:12px;margin:24px 0 0;padding:0}
.ae-trade__facts li{display:flex;gap:12px;font-size:.875rem;line-height:1.55;color:var(--ae-band-muted)}
.ae-trade__facts li::before{content:"";flex:none;width:6px;height:6px;border-radius:999px;
  background:var(--ae-band-accent);margin-top:7px}
.ae-trade .ae-btn{margin-top:24px}
.ae-trade .ae-btn--ghost{background:transparent;border-color:var(--ae-band-line-strong);color:var(--ae-band-ink)}
.ae-trade .ae-btn--ghost:hover{border-color:var(--ae-band-ink);color:var(--ae-band-ink)}
.ae-trade *:focus-visible{outline-color:var(--ae-band-focus)}

/* Quick order: a raised panel on the band, the part-number voice in mono. */
.ae-quick{background:var(--ae-band-raised);border:1px solid var(--ae-band-line);
  border-radius:var(--ae-radius-xl);padding:24px;box-shadow:var(--ae-shadow)}
.ae-quick label{display:block;margin-bottom:8px;font-size:1.0625rem;line-height:1.35;font-weight:650;
  letter-spacing:-.01em;color:var(--ae-band-ink)}
.ae-quick__row{display:flex;gap:8px;align-items:stretch}
.ae-quick textarea{flex:1;min-height:112px;padding:10px 12px;border:1px solid var(--ae-band-line-strong);
  border-radius:var(--ae-radius);background:var(--ae-band-field);color:var(--ae-band-ink);
  font:.8125rem/1.5 var(--font-family-mono,ui-monospace,Menlo,monospace);resize:vertical}
.ae-quick textarea::placeholder{color:var(--ae-band-muted)}
.ae-quick textarea:focus{outline:2px solid var(--ae-band-focus);outline-offset:0;border-color:var(--ae-band-focus)}
.ae-quick .ae-btn{width:100%;margin-top:8px}
.ae-quick__help{margin:10px 0 0;font:.6875rem/1.6 var(--font-family-mono,ui-monospace,Menlo,monospace);
  letter-spacing:.02em;color:var(--ae-band-muted)}
.ae-quick__results{margin:10px 0 0;padding:0;list-style:none;font-size:.875rem;color:var(--ae-band-muted)}
.ae-quick__results li{display:flex;flex-wrap:wrap;gap:8px;padding:6px 0;border-bottom:1px solid var(--ae-band-line)}
.ae-quick__results code{font:.75rem var(--font-family-mono,ui-monospace,Menlo,monospace);color:var(--ae-band-ink)}
.ae-quick__results a{color:var(--ae-band-ink);font-weight:600}
.ae-quick__ok{color:var(--ae-band-ink)}
.ae-quick__bad{color:var(--ae-band-accent)}
@media (max-width:900px){.ae-quick__row{flex-direction:column}}

/* ── Assurance register — the numbered trust band, Grid's numbering ──────────
 * An ink band in the dark mood; Horizon Day resolves it to a white band held
 * by its two hairlines (the same tokens carry both). Five entries sit in one
 * ruled row from 1024px, two columns on tablets, a numbered stack on phones. */
.ae-register-sec{background:var(--ae-register);color:var(--ae-band-ink);padding-block:clamp(48px,6vw,64px);
  border-top:1px solid var(--ae-band-line);border-bottom:1px solid var(--ae-band-line)}
.ae-register-sec .ae-eyebrow{color:var(--ae-band-accent);margin-bottom:12px}
.ae-register-sec *:focus-visible{outline-color:var(--ae-band-focus)}
.ae-register__title{margin:0 0 12px;font-size:clamp(1.625rem,1.1rem + 1.8vw,2.375rem);line-height:1.12;
  font-weight:680;letter-spacing:-.02em;color:var(--ae-band-ink);max-width:22em}
.ae-register__lede{margin:0 0 clamp(24px,4vw,48px);font-size:1.0625rem;line-height:1.5;
  color:var(--ae-band-muted);max-width:46em}
.ae-register{list-style:none;display:grid;grid-template-columns:1fr;gap:24px 0;margin:0;padding:0}
.ae-register li{border-top:1px solid var(--ae-band-line);padding:16px 0 0}
.ae-register__num{display:block;margin-bottom:12px;
  font:600 .8125rem/1.4 var(--font-family-mono,ui-monospace,Menlo,monospace);
  letter-spacing:.08em;color:var(--ae-band-accent);font-variant-numeric:tabular-nums}
.ae-register h3{margin:0 0 6px;font-size:.9375rem;font-weight:650;line-height:1.4;letter-spacing:-.005em;
  color:var(--ae-band-ink)}
.ae-register p{margin:0;font-size:.8438rem;line-height:1.55;color:var(--ae-band-muted)}
@media(min-width:768px){.ae-register{grid-template-columns:repeat(2,1fr);gap:24px}}
@media(min-width:1024px){
  .ae-register{grid-template-columns:repeat(5,1fr);gap:0}
  .ae-register li{border-top:0;padding:0 24px 0 0}
  .ae-register li + li{border-left:1px solid var(--ae-band-line);padding-left:24px}
}
`;
